import CounterInput from 'components/Common/Form/Input/CounterInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import noop from 'lib/function/noop'
import { rem } from 'polished'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { formatTransferTravellers, getValidTransferOptions } from './ExperienceTransferUtils'

const TabElement = styled.div`
  display: block;
  opacity: 1;
  transition: opacity 0.2s;

  &[hidden] {
    display: block;
    height: 0;
    margin: 0;
    opacity: 0;
    transition: opacity 0.2s, height 0s 0.2s;
    overflow: hidden;
  }
`

const CounterInputs = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: flex-end;
  }
`

const fadingCss = css`
  opacity: 1;
  height: 100%;
  transition: opacity 0.2s;

  &.hidden {
    height: 0;
    opacity: 0;
    margin: 0;
    pointer-events: none;
    transition: opacity 0.2s, height 0s 0.2s, margin 0s 0.2s;
    overflow: hidden;
  }
`

const FadingCounterInput = styled(CounterInput)`
  ${fadingCss}
`

const FadingCaption = styled(Caption)`
  ${fadingCss}
`

const BookingTravellers = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-columns: min-content;
  grid-auto-flow: column;
  align-items: center;
`

interface Props {
  transfer: App.ExperienceTransferView
  options: Array<App.ExperienceTransferOption>
  onChange?: (transfer: App.ExperienceTransferView) => void
  name?: string
  /** Override max passengers instead of calculating from options */
  maxPassengersOverride?: number
}

function ExperienceDetailsTransferBookingTravellers(props: Props) {
  const { transfer, onChange = noop, name, options, maxPassengersOverride } = props
  const intl = useIntl()
  const [editMode, setEditMode] = useState(false)
  const { travellers } = transfer

  const maxPassengers = useMemo(() => {
    if (maxPassengersOverride !== undefined) return maxPassengersOverride
    const transfers = getValidTransferOptions(transfer, options)
    return Math.max(...transfers.map(option => option.maxPassengers))
  }, [options, transfer, maxPassengersOverride])

  const onCounterChange = (value: number, input: HTMLInputElement) => {
    onChange({
      ...transfer,
      travellers: {
        ...transfer.travellers,
        [input.dataset.field!]: value,
      },
    })
  }

  const onSave = () => {
    setEditMode(false)
  }

  const hasChildren = !!travellers.children

  const travellersHeading = intl.formatMessage({
    id: 'eNBI/gljw1',
    defaultMessage: 'Travellers',
    description: 'Heading for travellers section in experience transfer booking',
  })
  const adultsLabel = intl.formatMessage({
    id: 'fdh0dmiROM',
    defaultMessage: 'Adults',
    description: 'Label for adults counter in experience transfer booking',
  })
  const adultsSubLabel = intl.formatMessage({
    id: 'ZOsNxyOngp',
    defaultMessage: '18+ yrs',
    description: 'Age range sub-label for adults in experience transfer booking',
  })
  const childrenLabel = intl.formatMessage({
    id: 'mRhwNyhUt1',
    defaultMessage: 'Children',
    description: 'Label for children counter in experience transfer booking',
  })
  const childrenSubLabel = intl.formatMessage({
    id: '6R4Z1EZc81',
    defaultMessage: '0-17 yrs',
    description: 'Age range sub-label for children in experience transfer booking',
  })
  const childSeatsLabel = intl.formatMessage({
    id: '0xZJjDQ3yO',
    defaultMessage: 'Child seats *',
    description: 'Label for child seats counter in experience transfer booking',
  })
  const boosterSeatsLabel = intl.formatMessage({
    id: 'uThXSeCWPm',
    defaultMessage: 'Booster seats *',
    description: 'Label for booster seats counter in experience transfer booking',
  })
  const saveTravellersLabel = intl.formatMessage({
    id: 'SYZabniXLZ',
    defaultMessage: 'Save travellers',
    description: 'Button text to save traveller selections in experience transfer booking',
  })
  const editLabel = intl.formatMessage({
    id: 'ovqfJxr0NQ',
    defaultMessage: 'Edit',
    description: 'Button text to edit traveller selections in experience transfer booking',
  })

  return <BookingTravellers>
    <VerticalSpacer gap={editMode ? 16 : 8}>
      <Heading variant="heading6">{travellersHeading}</Heading>
      <TabElement hidden={!editMode}>
        <VerticalSpacer gap={16}>
          <CounterInputs>
            <CounterInput
              label={adultsLabel}
              subLabel={adultsSubLabel}
              min={1}
              defaultValue={travellers.adults ?? 2}
              onChange={onCounterChange}
              data-field="adults"
              name={`${name}.travellers.adults`}
              max={maxPassengers - (travellers.children ?? 0)}
            />
            <CounterInput
              label={childrenLabel}
              subLabel={childrenSubLabel}
              defaultValue={travellers.children ?? 0}
              onChange={onCounterChange}
              data-field="children"
              name={`${name}.travellers.children`}
              max={maxPassengers - travellers.adults}
            />
            <FadingCounterInput
              label={childSeatsLabel}
              defaultValue={travellers.childSeats ?? 0}
              onChange={onCounterChange}
              data-field="childSeats"
              name={`${name}.travellers.childSeats`}
              className={!hasChildren ? 'hidden' : undefined}
            />
            <FadingCounterInput
              label={boosterSeatsLabel}
              defaultValue={travellers.boosterSeats ?? 0}
              onChange={onCounterChange}
              data-field="boosterSeats"
              name={`${name}.travellers.boosterSeats`}
              className={!hasChildren ? 'hidden' : undefined}
            />
          </CounterInputs>
          <FadingCaption variant="medium" colour="neutral-three" className={!hasChildren ? 'hidden' : undefined}>
            {intl.formatMessage({
              id: 'YhE8mvtsLZ',
              defaultMessage: '* Subject to availability and additional charges. For specific child seats leave a note in \u2018Special requests\u2019.',
              description: 'Disclaimer about child seat availability in experience transfer booking',
            })}
          </FadingCaption>
          <TextButton kind="secondary" onClick={onSave} fit="mobile-full-width">{saveTravellersLabel}</TextButton>
        </VerticalSpacer>
      </TabElement>
      <TabElement hidden={editMode}>
        <VerticalSpacer gap={4}>
          <BodyText variant="medium">
            {formatTransferTravellers(travellers, intl)}
          </BodyText>
          {!!travellers.children && <Caption variant="medium" colour="neutral-three">
            {intl.formatMessage({
              id: 'os4p99isNZ',
              defaultMessage: 'Edit travellers to request child seats, if required.',
              description: 'Hint text to edit travellers for child seat requests in experience transfer booking',
            })}
          </Caption>}
        </VerticalSpacer>
      </TabElement>
    </VerticalSpacer>
    {!editMode && <TextButton kind="tertiary" onClick={() => setEditMode(true)}>{editLabel}</TextButton>}
  </BookingTravellers>
}

export default ExperienceDetailsTransferBookingTravellers
